import {
  useState,
  useRef,
  useContext,
  type FormEvent,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Privacy } from "../contexts/privacy.ts";
import { api } from "../api.ts";
type FormMutation = <T>(
  path: string,
  body: unknown,
  method?: string,
) => Promise<T>;
export function ManagedForm({
  children,
  onSave,
  onDone,
  submitLabel = "Salvar registro",
}: {
  children: ReactNode;
  onSave: (fields: FormData, save: FormMutation) => Promise<unknown>;
  onDone: () => void;
  submitLabel?: string;
}) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    client = useQueryClient();
  const hidden = useContext(Privacy);
  const intent = useRef<{ fingerprint: string; key: string } | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const fields = new FormData(event.currentTarget);
      const fingerprint = JSON.stringify([...fields.entries()]);
      if (intent.current?.fingerprint !== fingerprint)
        intent.current = { fingerprint, key: crypto.randomUUID() };
      const key = intent.current.key;
      let savedPath = "";
      const save: FormMutation = (path, body, method) => {
        savedPath = path;
        return api(path, body, method, key);
      };
      await onSave(fields, save);
      const affected = savedPath.startsWith("/api/reserve")
        ? ["overview", "reserve", "transfers"]
        : savedPath.startsWith("/api/transfers")
          ? [
              "overview",
              "reserve",
              "transfers",
              "transactions",
              "record-history",
            ]
          : savedPath.startsWith("/api/planning")
            ? [
                "overview",
                "planning",
                "recurrences",
                "recurrence-history",
                "recurrence-preview",
                "occurrence-candidates",
                "record-history",
              ]
            : savedPath.startsWith("/api/transactions")
              ? [
                  "overview",
                  "transactions",
                  "bill-movements",
                  "occurrence-candidates",
                  "record-history",
                ]
              : null;
      await client.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] !== "session" &&
          (affected === null || affected.includes(String(query.queryKey[0]))),
      });
      onDone();
    } catch (e) {
      setError(
        e instanceof TypeError
          ? "A resposta não chegou. Tente salvar novamente com os mesmos dados para confirmar o registro."
          : e instanceof Error
            ? e.message
            : "Não foi possível salvar.",
      );
    } finally {
      setBusy(false);
    }
  }
  return hidden ? (
    <p>Mostre os valores antes de registrar informações.</p>
  ) : (
    <form className="form" onSubmit={submit}>
      {children}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button className="button primary" disabled={busy}>
        {busy ? "Salvando…" : submitLabel}
      </button>
    </form>
  );
}
